/**
 * Authentication Service for CNS Dashboard
 * Handles login, logout, token management, and user information
 */

import { API_URL } from '../config/apiConfig';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SystemUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  organization: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LoginResponse {
  token: string;
  user: SystemUser;
  mustChangePassword?: boolean;
}

export interface AuthError {
  error: string;
  code: string;
}

/**
 * Login with email and password
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await fetch(`${API_URL}/api/v1/admin/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error: AuthError = await response.json();
    throw new Error(
      (typeof error?.error === 'object' && (error as any).error?.message) ||
        (typeof error?.error === 'string' ? error.error : null) ||
        (error as any)?.message ||
        'Login failed'
    );
  }

  const data = await response.json();
  
  // Handle both admin-service format (access_token) and hotel-service format (token)
  const token = data.token || data.access_token;
  // Normalize snake_case API user fields for the UI
  const rawUser = data.user || {};
  const user: SystemUser = {
    id: rawUser.id,
    email: rawUser.email,
    fullName: rawUser.fullName || rawUser.full_name || rawUser.name || '',
    role: rawUser.role,
    organization: rawUser.organization,
    isActive: rawUser.isActive ?? rawUser.is_active ?? true,
    lastLogin: rawUser.lastLogin || rawUser.last_login,
    createdAt: rawUser.createdAt || rawUser.created_at,
    updatedAt: rawUser.updatedAt || rawUser.updated_at,
  };
  
  // Store token and user info in localStorage
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  
  // Legacy flag is intentionally cleared: password resets do not block login.
  localStorage.removeItem('mustChangePassword');
  
  return { token, user, mustChangePassword: false } as LoginResponse;
};

/**
 * Logout and clear stored credentials
 */
export const logout = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('mustChangePassword');
};

/**
 * Get stored token
 */
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Get current user from localStorage
 */
export const getCurrentUserFromStorage = (): SystemUser | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) {
    return null;
  }
  
  try {
    return JSON.parse(userStr) as SystemUser;
  } catch {
    return null;
  }
};

/**
 * Fetch current user from API
 */
export const getCurrentUser = async (): Promise<SystemUser> => {
  const token = getToken();
  
  if (!token) {
    throw new Error('No authentication token');
  }

  const response = await fetch(`${API_URL}/api/v1/admin/auth/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      logout();
      throw new Error('Session expired');
    }
    const error: AuthError = await response.json();
    throw new Error(error.error || 'Failed to fetch user');
  }

  const user: SystemUser = await response.json();
  
  // Update stored user info
  localStorage.setItem('user', JSON.stringify(user));
  
  return user;
};

function isJwtExpired(token: string): boolean {
  try {
    const [, payload] = token.split('.');
    if (!payload) return true;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const data = JSON.parse(atob(padded));
    if (typeof data.exp !== 'number') return true;
    return Date.now() >= data.exp * 1000 - 30_000;
  } catch {
    return true;
  }
}

/**
 * Check if user is authenticated (non-expired JWT + stored user).
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  const user = getCurrentUserFromStorage();
  if (!token || !user) return false;
  if (isJwtExpired(token)) {
    logout();
    return false;
  }
  return true;
};

/**
 * Check if user has specific role
 */
export const hasRole = (role: string): boolean => {
  const user = getCurrentUserFromStorage();
  return user?.role === role;
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (roles: string[]): boolean => {
  const user = getCurrentUserFromStorage();
  return user ? roles.includes(user.role) : false;
};

/**
 * Check if user must change password
 */
export const mustChangePassword = (): boolean => {
  return localStorage.getItem('mustChangePassword') === 'true';
};

/**
 * Clear must change password flag
 */
export const clearMustChangePassword = (): void => {
  localStorage.removeItem('mustChangePassword');
};

/**
 * Change password for the authenticated system user (ANR/CNS staff).
 * Requires current password; new password must meet strength rules (8+, upper, number).
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const user = getCurrentUserFromStorage();
  const response = await fetch(`${API_URL}/api/v1/admin/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      email: user?.email,
      old_password: currentPassword,
      new_password: newPassword,
    }),
  });

  if (!response.ok) {
    let message = 'Password change failed';
    try {
      const error: AuthError & { code?: string } = await response.json();
      message = error.error || message;
      if (error.code === 'WEAK_PASSWORD') {
        message =
          'Le nouveau mot de passe doit contenir au moins 8 caractères, une majuscule et un chiffre.';
      } else if (error.code === 'INVALID_OLD_PASSWORD') {
        message = 'Mot de passe actuel incorrect.';
      }
    } catch {
      // keep default message
    }
    throw new Error(message);
  }

  clearMustChangePassword();
};

export const authService = {
  login,
  logout,
  getToken,
  getCurrentUser,
  getCurrentUserFromStorage,
  isAuthenticated,
  hasRole,
  hasAnyRole,
  mustChangePassword,
  clearMustChangePassword,
  changePassword,
};

export default authService;
